import { useNavigate } from 'react-router-dom';
import React, { useCallback, useState } from 'react';
import {
  AccountType,
  ICSPRClickSDK,
  SignResult,
  WALLET_KEYS
} from '@make-software/csprclick-core-types';

import { telegramClientApi, useMe } from '@/shared';
import {
  buildRedirectLink,
  getDevicePlatform,
  getEncodedAuthData,
  openLink,
  shouldBeRedirectedToCWM
} from '@/utils';
import { PLATFORM, WALLET_URLS } from '@/constants';
import { useAuthData } from '@/features';
import { PostWalletConnectionRequestType } from '@/entities';

interface UseWalletConnectionArgs {
  onWalletConnected?: (account: AccountType) => void;
}

export const useWalletConnection = ({ onWalletConnected }: UseWalletConnectionArgs = {}) => {
  const navigate = useNavigate();
  const meService = useMe();
  const [eventRegistered, setEventRegistered] = useState(false);
  const authData = useAuthData();
  const initData = authData.initData || '';

  const clickRef = window.csprclick as ICSPRClickSDK;
  const [isWalletConnected, setIsWalletConnected] = React.useState(false);

  // wallet is only fully connected when it's connected to CSPR.click and verified on backend
  const connectedAccount = meService.data?.wallet?.public_key || null;
  const currentAccount = clickRef?.getActiveAccount();
  const isCsprclickConnected = currentAccount && connectedAccount === currentAccount.public_key;

  if (isCsprclickConnected)
    clickRef?.isConnected(currentAccount.provider).then((isConnected) => {
      setIsWalletConnected((prev) => {
        if (!prev && !!isConnected) {
          onWalletConnected?.(currentAccount);
        }
        return !!isConnected;
      });
    });
  else if (isWalletConnected) setIsWalletConnected(false);

  const connectWallet = useCallback(async () => {
    const csprclick = (window as any).csprclick as ICSPRClickSDK;
    const platform = getDevicePlatform();
    const isInAppBrowser = navigator.userAgent.includes('WebView CasperWalletMobile');
    const encodedData = getEncodedAuthData(initData);

    if (connectedAccount) {
      // Disconnect wallet
      await telegramClientApi.postWalletDisconnection();
      try {
        const account = await csprclick.getActiveAccountAsync();
        if (account?.provider === WALLET_KEYS.CASPER_WALLET) {
          csprclick.signOut();
          await csprclick.disconnect('casper-wallet');
        } else {
          csprclick.signOut();
        }
      } catch (err: any) {
        console.error('Error disconnecting from wallet: ', err);
      }

      if (shouldBeRedirectedToCWM()) {
        const redirectLink = buildRedirectLink(platform, window.location.origin, encodedData);
        openLink(redirectLink)();
      }
      meService.refresh();
      return;
    }

    if (isInAppBrowser) {
      await requestConnection();
    } else if (platform.includes(PLATFORM.IOS) || platform.includes(PLATFORM.ANDROID)) {
      // if app runs on mobile outside of the in-app browser, use deep/universal link to redirect to CWM
      const redirectLink = buildRedirectLink(platform, window.location.origin, encodedData);
      openLink(redirectLink)();
    } else {
      await requestConnectionFromDesktop();
      // navigate(CSPR_CLICK_CONNECTION_PATH);
    }
  }, [connectedAccount, initData, navigate, meService]);

  const downloadWallet = useCallback(async () => {
    await telegramClientApi.postWalletDownloaded();

    const platform = getDevicePlatform();
    const downloadUrl = platform.includes(PLATFORM.IOS)
      ? WALLET_URLS.CASPER_WALLET
      : WALLET_URLS.DOWNLOAD;

    if (platform.includes(PLATFORM.IOS)) {
      window.location.href = downloadUrl;
    } else {
      openLink(downloadUrl)();
    }
  }, []);

  const signMessage = async (connectedAccount: AccountType) => {
    const csprclick = (window as any).csprclick as ICSPRClickSDK;
    const result = await telegramClientApi.getWalletConnection(
      connectedAccount.public_key,
      initData
    );

    if (result.data?.message) {
      const signResult: SignResult | undefined = await csprclick.signMessage(
        result.data.message,
        connectedAccount.public_key
      );
      if (!signResult?.cancelled && signResult?.signatureHex) {
        const proof: PostWalletConnectionRequestType = {
          account: connectedAccount.public_key,
          provider: 'casper-wallet',
          message: result.data?.message,
          signature: signResult.signatureHex,
        };
        const postConnectResult = await telegramClientApi.postWalletConnection(
          proof,
          initData
        );

        if (postConnectResult.data?.wallet.public_key) {
          console.debug('Signature verified on backend. Redirecting to Telegram');
          meService.refresh();
          onWalletConnected?.(connectedAccount);
        } else {
          console.debug('SIGN ERROR2: ' + JSON.stringify(postConnectResult));
          alert('Sign error: ' + postConnectResult.error?.message);
        }
      } else {
        alert('To complete connection, please, sign the message.');
      }
    } else {
      console.debug('RESPONSE ERROR: ' + JSON.stringify(result));
      alert('Error: message request invalid. ' + result.error?.message);
    }
  };

  const requestConnection = async () => {
    const csprclick = (window as any).csprclick as ICSPRClickSDK;
    csprclick
      .connect('casper-wallet')
      .then(async (account: AccountType | undefined) => {
        console.debug('ACCOUNT: ' + account);

        let connectedAccount: AccountType | undefined = account;
        if (connectedAccount) {
          await signMessage(connectedAccount);
        } else {
          setTimeout(async () => {
            connectedAccount =
              (await csprclick.getActiveAccountAsync({ withBalance: true })) || undefined;
            if (connectedAccount) {
              await signMessage(connectedAccount);
            } else {
              alert('Error: Account not connected. Please, try again.');
            }
          }, 300);
        }
        return;
      })
      .catch((err: any) => {
        console.debug('CONNECTION ERROR: ' + JSON.stringify(err));
        alert('Connection error: ' + err.message);
      });
  };

  const requestConnectionFromDesktop = async () => {
    const csprclick = (window as any).csprclick as ICSPRClickSDK;

    if (!eventRegistered) {
      setEventRegistered(true);
      csprclick.on('csprclick:signed_in', async (evt: any) => {
        if (evt.account.public_key) {
          await signMessage(evt.account);
        }
      });
    }
    csprclick.signIn();
  };

  return {
    connectedAccount,
    isWalletConnected,
    isCsprclickConnected,
    connectWallet,
    downloadWallet
  };
};
